import {
  boolean,
  char,
  date,
  pgEnum,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
  varchar,
} from "drizzle-orm/pg-core"
import { estates } from "./estate"

// Lookup Tables

// Purpose: Lists states or provinces for addresses.
export const stateProvinceLists = pgTable(
  "state_province_list",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: varchar("name", { length: 100 }).notNull().unique(),
    abbreviation: varchar("abbreviation", { length: 10 }).unique(),
    country: char("countrycode", { length: 2 }).notNull().default("US"),
  },
  (table) => [unique("abbreviation_country_unique").on(table.abbreviation, table.country)],
)

// enums

export const contactTypeEnum = pgEnum("contact_type_enum", [
  "SPOUSE_OR_PARTNER",
  "FORMER_SPOUSE",
  "CHILD",
  "STEPCHILD",
  "FOSTER_CHILD",
  "GRANDCHILD",
  "PROFESSIONAL_SERVICE",
  "AUNT",
  "UNCLE",
  "MOTHER",
  "FATHER",
  "MOTHER_IN_LAW",
  "FATHER_IN_LAW",
  "STEP_MOTHER",
  "STEP_FATHER",
  "GRANDMOTHER",
  "GRANDFATHER",
  "NIECE",
  "NEPHEW",
  "OTHER_FAMILY",
  "FRIEND",
  "COLLEAGUE",
  "OTHER",
  "NONE",
])

export const maritalStatusEnum = pgEnum("marital_status_enum", [
  "MARRIED",
  "CIVIL_UNION",
  "COMMON_LAW",
  "DIVORCED",
  "LEGALLY_SEPARATED",
  "WIDOWED",
  "SINGLE",
  "ENGAGED",
  "DATING",
  "COHABITATING",
  "OTHER",
])

export const estateRoleEnum = pgEnum("estate_role_enum", [
  "EXECUTOR",
  "TRUSTEE",
  "GUARDIAN",
  "BUSINESS PARTNER",
  "PET_CARETAKER",
  "POWER_OF_ATTORNEY",
  "HEALTH_CARE_PROXY",
  "DIGITAL_EXECUTOR",
  "PERSONAL_REPRESENTATIVE",
  "PROFESSIONAL_ADVISOR",
  "FINANCIAL_ADVISOR",
  "TAX_PROFESSIONAL",
  "LEGAL_PROFESSIONAL",
  "CO-OWNER",
  "FUNERAL_DIRECTOR",
  "EXCLUDED",
])

export const rolePositionEnum = pgEnum("role_position_enum", [
  "PRIMARY",
  "SECONDARY",
  "TERTIARY",
  "QUARTARY",
])

export const insuranceRoleEnum = pgEnum("insurance_role_enum", [
  "LIFE_INSURED",
  "PAYER_OF_POLICY",
  "POLICY_OWNER",
])

export const relationshipTypeEnum = pgEnum("relationship_type_enum", [
  "BIOLOGICAL_PARENT",
  "ADOPTIVE_PARENT",
  "STEP_PARENT",
  "GUARDIAN",
])

// Core Tables

// Purpose: Stores detailed address information, reusable by contacts, companies, etc.
// A location can be scoped to either an estate OR an organization (not both).
// At least one of estateId or organizationId must be set (enforced at application level).
// Note: organizationId foreign key is defined in company.ts to avoid circular dependency
export const locations = pgTable("location", {
  id: uuid("id").primaryKey().defaultRandom(),
  estateId: uuid("estate_id").references(() => estates.id, { onDelete: "cascade" }),
  organizationId: uuid("organization_id"), // Foreign key defined in company.ts
  addressLine1: varchar("address_line_1", { length: 255 }).notNull(),
  addressLine2: varchar("address_line_2", { length: 255 }),
  city: varchar("city", { length: 100 }).notNull(),
  stateProvinceId: uuid("state_province_id").references(() => stateProvinceLists.id),
  county: varchar("county", { length: 100 }),
  zipPostalCode: varchar("zip_postal_code", { length: 20 }).notNull(),
  country: varchar("country", { length: 100 }).notNull(),
  isDraft: boolean("is_draft").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Explicitly links a contact to an estate with a specific role within that estate.
export const estateContactRoles = pgTable(
  "estate_contact_role",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    estateId: uuid("estate_id")
      .notNull()
      .references(() => estates.id, { onDelete: "cascade" }),
    contactId: uuid("contact_id")
      .notNull()
      .references(() => contacts.id, { onDelete: "cascade" }),
    estateRole: estateRoleEnum("estate_role_enum"),
    rolePosition: rolePositionEnum("role_position_enum"),
    // additional: need to add profile pictures assuming will be linked to an S3 bucket or similar
    profilePictureUrl: varchar("profile_picture_url", { length: 512 }),
    estateInstructions: text("estate_instructions"),
    startDate: date("start_date"),
    endDate: date("end_date"),
    notes: text("notes"),
    isDraft: boolean("is_draft").default(false).notNull(),
  },
  (table) => [
    unique("estate_contact_role_unique").on(table.estateId, table.contactId, table.estateRole),
  ],
)

// Purpose: Designates a contact as trusted to request account access for an estate in an emergency or upon death.
export const emergencyContacts = pgTable(
  "emergency_contact",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    estateId: uuid("estate_id")
      .notNull()
      .references(() => estates.id, { onDelete: "cascade" }),
    contactId: uuid("contact_id")
      .notNull()
      .references(() => contacts.id, { onDelete: "cascade" }),
    description: text("description"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    unique("emergency_contact_estate_contact_unique").on(table.estateId, table.contactId),
  ],
)

// Purpose: Stores information for individuals, whether they are app users, family members, beneficiaries, or external advisors.
export const contacts = pgTable("contact", {
  id: uuid("id").primaryKey().defaultRandom(),
  contactType: contactTypeEnum("contact_type_enum"),
  estateId: uuid("estate_id").references(() => estates.id, { onDelete: "cascade" }),
  firstName: varchar("first_name", { length: 100 }),
  lastName: varchar("last_name", { length: 100 }),
  email: varchar("email", { length: 255 }),
  phoneNumber: varchar("phone_number", { length: 50 }),
  dateOfBirth: date("date_of_birth"),
  dateOfDeath: date("date_of_death"),
  maritalStatus: maritalStatusEnum("marital_status_enum"),
  maritalStatusDate: date("marital_status_date"),
  relationshipStartDate: date("relationship_start_date"),
  relationshipEndDate: date("relationship_end_date"),
  insuranceRole: insuranceRoleEnum("insurance_role_enum"),
  locationId: uuid("location_id").references(() => locations.id, { onDelete: "set null" }),
  isDraft: boolean("is_draft").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Links a child contact to a parent contact within an estate with a specific relationship type.
export const contactRelationships = pgTable(
  "contact_relationship",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    parentContactId: uuid("parent_contact_id")
      .notNull()
      .references(() => contacts.id, { onDelete: "cascade" }),
    childContactId: uuid("child_contact_id")
      .notNull()
      .references(() => contacts.id, { onDelete: "cascade" }),
    relationshipType: relationshipTypeEnum("relationship_type").notNull(),
    estateId: uuid("estate_id")
      .notNull()
      .references(() => estates.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    unique("contact_relationship_unique").on(
      table.parentContactId,
      table.childContactId,
      table.estateId,
    ),
  ],
)
